import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0A1F3B]" data-testid="privacy-page">
      <nav className="sticky top-0 z-50 bg-[#0A1F3B]/80 backdrop-blur-xl border-b border-[#1C3D66]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-1">
            <span className="font-heading text-2xl font-light text-[#F5D300]">oly</span>
            <span className="font-heading text-2xl font-black text-white">Payroll</span>
          </Link>
          <Link to="/" className="text-sm text-[#94A3B8] hover:text-white flex items-center gap-1"><ArrowLeft size={14} /> Back</Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-white mb-2">Privacy Policy</h1>
        <p className="text-sm text-[#94A3B8] mb-10">Last updated: May 1, 2026</p>

        <div className="prose-legal space-y-8 text-sm text-[#94A3B8] leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-white mb-2">1. Introduction</h2>
            <p>oly Payroll (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates the website olypayroll.com and related services (the &ldquo;Service&rdquo;). This Privacy Policy explains how we collect, use, disclose, and protect your information when you use our Service.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">2. Information We Collect</h2>
            <p className="mb-2"><span className="text-white font-semibold">Account Information:</span> When you create an account, we collect your name, email address, and password (stored as a salted hash).</p>
            <p className="mb-2"><span className="text-white font-semibold">Payroll Data:</span> When you upload Clover POS shift exports, we process employee names, shift times, hours worked, and tip amounts. This data is used solely to generate your payroll reports.</p>
            <p className="mb-2"><span className="text-white font-semibold">Payment Information:</span> Payment processing is handled by Stripe. We do not store credit card numbers. We receive your subscription status and transaction IDs from Stripe.</p>
            <p className="mb-2"><span className="text-white font-semibold">Usage Data:</span> We collect anonymized analytics via Google Analytics (GA4) including pages visited, features used, and device type. No personally identifiable information is sent to Google Analytics.</p>
            <p><span className="text-white font-semibold">Device Data (Clover Bridge):</span> If you use our Clover POS Bridge app, we collect device identifiers, merchant IDs, and shift data synced from your Clover device. This data is used solely for payroll processing.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-1">
              <li>To provide, maintain, and improve the Service</li>
              <li>To process payroll data and generate reports</li>
              <li>To process payments and manage subscriptions</li>
              <li>To send transactional emails (report confirmations, account notifications)</li>
              <li>To provide customer support</li>
              <li>To detect and prevent fraud or abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">4. Data Storage and Security</h2>
            <p>Your data is stored on secure, encrypted servers. Passwords are hashed using bcrypt. Authentication tokens are transmitted via secure HTTP-only cookies. We implement industry-standard security measures to protect your data, but no method of electronic transmission or storage is 100% secure.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">5. Data Retention</h2>
            <p>Account data is retained for as long as your account is active. Payroll reports are retained for your access until you delete your account. You may request deletion of your account and all associated data by contacting support@olypayroll.com.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">6. Third-Party Services</h2>
            <p className="mb-2">We use the following third-party services:</p>
            <ul className="list-disc list-inside space-y-1">
              <li><span className="text-white">Stripe</span> — Payment processing (see <a href="https://stripe.com/privacy" className="text-[#F5D300] hover:underline" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy</a>)</li>
              <li><span className="text-white">Google Analytics</span> — Anonymized usage analytics (see <a href="https://policies.google.com/privacy" className="text-[#F5D300] hover:underline" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>)</li>
              <li><span className="text-white">Resend</span> — Transactional email delivery</li>
              <li><span className="text-white">MongoDB</span> — Database hosting</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">7. Cookies</h2>
            <p>We use essential cookies for authentication (login session) and a usage-tracking cookie to manage the freemium paywall. We do not use advertising or tracking cookies beyond Google Analytics.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">8. Your Rights</h2>
            <p>You have the right to access, correct, or delete your personal data. You may export your payroll reports at any time. To exercise these rights, contact support@olypayroll.com.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">9. Children&apos;s Privacy</h2>
            <p>Our Service is not directed to individuals under 18. We do not knowingly collect personal information from children.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on this page with a revised &ldquo;Last updated&rdquo; date.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">11. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, contact us at <a href="mailto:support@olypayroll.com" className="text-[#F5D300] hover:underline">support@olypayroll.com</a>.</p>
          </section>
        </div>
      </main>

      <footer className="border-t border-[#1C3D66] py-6 px-6 sm:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="font-heading text-lg font-light text-[#F5D300]">oly</span>
            <span className="font-heading text-lg font-black text-white">Payroll</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-[#475569]">
            <Link to="/privacy" className="hover:text-[#94A3B8]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#94A3B8]">Terms of Use</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
